/* global debounce, trapFocus */

if (!customElements.get('cart-items')) {
  class CartItems extends HTMLElement {
    constructor() {
      super();
      if (this.dataset.empty === 'false') this.init();
    }

    init() {
      this.fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      this.cartDrawer = this.closest('cart-drawer');
      this.itemStatus = this.querySelector('#cart-line-item-status');
      this.currentTotalItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
        (total, quantityInput) => total + parseInt(quantityInput.value, 10),
        0
      );

      this.currentQuantities = [];
      this.querySelectorAll('.cart-item').forEach((item) => {
        this.currentQuantities[item.dataset.variantId] = Number(item.querySelector('.qty-input__input').value);
      });

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', debounce(this.handleChange.bind(this)));

      if (!this.cartDrawer) {
        this.bindDiscountSubtotalSync();
        this.startSubtotalObserver();
      }
    }

    disconnectedCallback() {
      if (this.discountApplyClickHandler) {
        document.removeEventListener('click', this.discountApplyClickHandler);
      }

      if (this.discountApplyKeydownHandler) {
        document.removeEventListener('keydown', this.discountApplyKeydownHandler);
      }

      if (this.subtotalObserver) {
        this.subtotalObserver.disconnect();
      }
    }

    bindDiscountSubtotalSync() {
      if (this.discountApplyClickHandler || this.discountApplyKeydownHandler) return;

      this.discountApplyClickHandler = (evt) => {
        if (!evt.target.closest('#bss-b2b-discount-code-submit')) return;
        this.scheduleSubtotalSync();
      };

      this.discountApplyKeydownHandler = (evt) => {
        if (evt.key !== 'Enter') return;
        if (!evt.target.matches('#bss-b2b-discount-code-input')) return;
        this.scheduleSubtotalSync();
      };

      document.addEventListener('click', this.discountApplyClickHandler);
      document.addEventListener('keydown', this.discountApplyKeydownHandler);
    }

    scheduleSubtotalSync() {
      [300, 800, 1400, 2200].forEach((delay) => {
        window.setTimeout(() => {
          if (!document.body.contains(this)) return;
          this.syncSubtotalFromLines();
        }, delay);
      });
    }

    startSubtotalObserver() {
      if (this.subtotalObserver) return;

      this.subtotalObserver = new MutationObserver(() => {
        this.syncSubtotalFromLines();
      });

      this.subtotalObserver.observe(this, {
        subtree: true,
        childList: true,
        characterData: true
      });
    }

    syncSubtotalFromLines() {
      const subtotalEl = document.getElementById('cart-summary-subtotal');
      if (!subtotalEl) return;

      const lineTotalEls = this.querySelectorAll('.cart-item__total .price__current, .cart-item__total strong.price__current');
      if (!lineTotalEls.length) return;

      const lineSumCents = Array.from(lineTotalEls).reduce((sum, el) => {
        const cents = this.parseMoneyTextToCents(el.textContent);
        return Number.isFinite(cents) ? sum + cents : sum;
      }, 0);

      if (!Number.isFinite(lineSumCents) || lineSumCents <= 0) return;
      const discountCents = this.getAppliedDiscountCents();
      const effectiveSubtotal = Math.max(0, lineSumCents - discountCents);
      subtotalEl.textContent = this.formatMoneyFromTemplate(effectiveSubtotal, subtotalEl.textContent);
    }

    getAppliedDiscountCents() {
      const discountArea = document.querySelector('.bss-b2b-discount-code-wrapper');
      if (!discountArea) return 0;

      const text = discountArea.textContent || '';
      const matches = text.match(/-\s*CHF\s*[\d'.,]+/g);
      if (!matches || !matches.length) return 0;

      return matches.reduce((sum, raw) => {
        const cents = this.parseMoneyTextToCents(raw);
        return Number.isFinite(cents) ? sum + Math.abs(cents) : sum;
      }, 0);
    }

    parseMoneyTextToCents(text) {
      if (!text) return NaN;
      // Extract a single money token to avoid concatenating multiple values
      // from the same text node (e.g. current + compare-at price).
      const tokenMatch = text.match(/-?\d{1,3}(?:[.'’\s]\d{3})*(?:[.,]\d{2})|-?\d+(?:[.,]\d{2})/);
      if (!tokenMatch) return NaN;

      const token = tokenMatch[0];
      const sign = token.includes('-') ? -1 : 1;
      const cleaned = token.replace(/[’'\s]/g, '');

      const lastComma = cleaned.lastIndexOf(',');
      const lastDot = cleaned.lastIndexOf('.');
      const decimalSep = lastComma > lastDot ? ',' : '.';
      const decimalIdx = cleaned.lastIndexOf(decimalSep);

      if (decimalIdx === -1) return sign * Number(cleaned.replace(/[^\d]/g, '')) * 100;

      const intPart = cleaned.slice(0, decimalIdx).replace(/[^\d]/g, '');
      const decPart = cleaned.slice(decimalIdx + 1).replace(/[^\d]/g, '').padEnd(2, '0').slice(0, 2);
      return sign * (Number(intPart || '0') * 100 + Number(decPart || '0'));
    }

    formatMoneyFromTemplate(cents, template) {
      const reference = template || '';
      const currencyMatch = reference.match(/[A-Z]{3}/);
      const currency = currencyMatch ? currencyMatch[0] : 'CHF';

      const amount = Math.abs(cents) / 100;
      const formattedNumber = new Intl.NumberFormat('de-CH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount).replace(/\u2019/g, "'");

      const signed = cents < 0 ? `-${formattedNumber}` : formattedNumber;
      return `${currency}${signed}`;
    }

    /**
     * Dispatches a custom event and isolates third-party listener errors.
     * @param {string} eventName - Event name to dispatch.
     * @param {object} detail - Event detail payload.
     */
    dispatchSafeEvent(eventName, detail) {
      try {
        this.dispatchEvent(new CustomEvent(eventName, {
          bubbles: true,
          detail
        }));
      } catch (error) {
        console.warn(`CartItems: listener error for ${eventName}`, error); // eslint-disable-line
      }
    }

    /**
     * Handles 'click' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-remove-item')) return;
      evt.preventDefault();
      this.updateQuantity(evt.target.dataset.index, 0);
    }

    /**
     * Handles 'change' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      this.updateQuantity(evt.target.dataset.index, evt.target.value, document.activeElement.name);
    }

    /**
     * Updates the quantity of a line item.
     * @param {number} line - Line item index.
     * @param {number} quantity - Quantity to set.
     * @param {string} name - Active element name.
     */
    async updateQuantity(line, quantity, name) {
      const cartDrawerContent = this.cartDrawer ? this.cartDrawer.querySelector('.drawer__content') : null;
      const cartDrawerContentScroll = cartDrawerContent ? cartDrawerContent.scrollTop : 0;
      const cartDrawerScroll = this.cartDrawer ? this.cartDrawer.scrollTop : 0;

      this.enableLoading(line);

      // clear all errors except this line's (which will be refreshed after this update)
      const lineErrorsId = `line-item-error-${line}`;
      const lineErrors = this.querySelector(`#${lineErrorsId}`);
      this.querySelectorAll(`.cart-errors, .cart-item__error:not([id="${lineErrorsId}"])`).forEach((el) => {
        el.innerHTML = '';
        el.hidden = true;
      });

      const sections = this.getSectionsToRender().map((section) => section.section);
      this.fetchRequestOpts.body = JSON.stringify({
        line,
        quantity,
        sections: [...new Set(sections)],
        sections_url: window.location.pathname
      });

      try {
        const lineItem = this.querySelector(`#cart-item-${line}`);
        const variantId = lineItem ? Number(lineItem.dataset.variantId) : null;
        const oldTotalQuantity = this.currentTotalItemCount;
        const response = await fetch(theme.routes.cartChange, this.fetchRequestOpts);
        const data = await response.json();

        if (!response.ok) throw new Error(data.errors || response.status);

        const newTotalQuantity = data.item_count;

        if (this.cartDrawer) {
          if (cartDrawerContent) {
            cartDrawerContent.classList.toggle('drawer__content--flex', newTotalQuantity === 0);
          }

          if (newTotalQuantity === 0) {
            const recommendations = this.cartDrawer.querySelector('product-recommendations');
            if (recommendations) recommendations.remove();
          }
        } else if (newTotalQuantity === 0) {
          // We're on the Cart page
          const cartTitle = this.closest('.cc-main-cart').querySelector('.js-cart-title');
          if (cartTitle) cartTitle.style.textAlign = 'center';

          const cartSummaryCss = document.getElementById('cart-summary-css');
          if (cartSummaryCss) cartSummaryCss.remove();

          const cartSummary = document.getElementById('cart-summary');
          if (cartSummary) cartSummary.hidden = true;
        }

        this.getSectionsToRender().forEach((section) => {
          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) return;

          const { selector } = section;
          const el = sectionEl.querySelector(selector) || sectionEl;
          const nextHtml = CartItems.getElementHTML(data.sections[section.section], selector);
          // Bugfix ShopStrategen: never overwrite visible cart UI with invalid/empty section payload.
          if (nextHtml === null) return;
          el.innerHTML = nextHtml;
        });

        if (this.cartDrawer && newTotalQuantity === 0) {
          if (cartDrawerContent) {
            cartDrawerContent.classList.add('grow', 'flex', 'items-center');
          }

          if (this.cartDrawer.querySelector('promoted-products')) {
            this.cartDrawer
              .querySelector('.drawer__content')
              .classList.toggle('drawer__empty-with-promotions', newTotalQuantity === 0);
          }
        }

        this.updateRecommendations(data.item_count > 0 ? data.items[0].product_id : null);
        this.updateLiveRegions();
        this.setFocus(line, newTotalQuantity, name);
        this.dataset.empty = newTotalQuantity === 0;
        this.currentTotalItemCount = newTotalQuantity;

        // Fire the on:line-item:change event if the line item quantity has changed
        if (oldTotalQuantity !== newTotalQuantity && variantId) {
          this.dispatchSafeEvent('on:line-item:change', {
            cart: data,
            variantId,
            oldQuantity: this.currentQuantities[variantId],
            newQuantity: Number(quantity)
          });
        }

        if (variantId) this.currentQuantities[variantId] = Number(quantity);

        if (lineErrors) {
          lineErrors.innerHTML = '';
          lineErrors.hidden = true;
        }
      } catch (error) {
        if (lineErrors) {
          if (/^[0-9]+$/.test(error.message)) {
            lineErrors.textContent = theme.strings.cartError;
          } else {
            lineErrors.textContent = error.message;
          }
          lineErrors.hidden = false;
        }
        console.log(error); // eslint-disable-line

        this.querySelectorAll('.cart-item__loader').forEach((loader) => {
          loader.hidden = true;
        });

        this.dispatchSafeEvent('on:cart:error', {
          error: error.message
        });

        const input = this.querySelector(`#quantity-${line}`);
        if (input) {
          input.value = input.dataset.initialValue;
          const quantityInput = input.closest('quantity-input');
          if (quantityInput) quantityInput.currentQty = input.dataset.initialValue;
        }
      } finally {
        this.classList.remove('pointer-events-none');

        // Attempt to maintain the same scroll position in the cart drawer
        if (cartDrawerContent) {
          requestAnimationFrame(() => { cartDrawerContent.scrollTop = cartDrawerContentScroll; });
          setTimeout(() => { cartDrawerContent.scrollTop = cartDrawerContentScroll; }, 0);
          requestAnimationFrame(() => { this.cartDrawer.scrollTop = cartDrawerScroll; });
          setTimeout(() => { this.cartDrawer.scrollTop = cartDrawerScroll; }, 0);
        }
      }
    }

    /**
     * Refreshes the cart by rerendering its sections and updating its product recommendations.
     */
    async refresh() {
      const errors = this.querySelector('#cart-errors') || document.getElementById('cart-errors');
      try {
        const sections = this.getSectionsToRender().map((section) => section.section);
        const response = await fetch(`?sections=${[...new Set(sections)]}`);

        // The status is 400, refresh the whole cart and stop
        if (response.status === 400 && this.cartDrawer) {
          this.cartDrawer.refresh(true);
          return;
        }

        const data = await response.json();

        if (!response.ok) throw new Error(response.status);

        this.getSectionsToRender().forEach((section) => {
          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) return;

          const el = sectionEl.querySelector(section.selector) || sectionEl;
          const nextHtml = CartItems.getElementHTML(data[section.section], section.selector);
          if (nextHtml === null) return;
          el.innerHTML = nextHtml;
        });

        const firstCartItem = this.querySelector('.cart-item:first-child');
        this.updateRecommendations(firstCartItem ? firstCartItem.dataset.productId : null);

        errors.innerHTML = '';
        errors.hidden = true;
      } catch (error) {
        errors.textContent = theme.strings.cartError;
        errors.hidden = false;
        console.log(error); // eslint-disable-line

        this.dispatchSafeEvent('on:cart:error', {
          error: error.message
        });
      }
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      let sections = [
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        },
        {
          id: 'free-shipping-notice',
          section: 'free-shipping-notice',
          selector: '.free-shipping-notice'
        }
      ];

      if (this.cartDrawer) {
        const cartDrawerId = this.dataset.section
          || this.cartDrawer?.closest('.shopify-section')?.id?.replace('shopify-section-', '');
        if (!cartDrawerId) return sections;
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: cartDrawerId,
            selector: 'cart-items'
          },
          {
            id: 'cart-promoted-products',
            section: cartDrawerId,
            selector: '#cart-promoted-products'
          },
          {
            id: 'cart-drawer',
            section: cartDrawerId,
            selector: '.cart-drawer__summary'
          },
          {
            id: 'cart-drawer-media-promotion',
            section: cartDrawerId,
            selector: '#cart-drawer-media-promotion'
          }
        ];
      } else {
        const cartSummary = document.getElementById('cart-summary');
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: this.dataset.section,
            selector: 'cart-items'
          },
          {
            id: 'cart-summary',
            section: cartSummary ? cartSummary.dataset.section : this.dataset.section,
            selector: '.cart__summary'
          }
        ];
      }

      return sections;
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element to get the innerHTML of.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      // Bugfix ShopStrategen: return null when selector is missing so callers can keep existing DOM.
      if (!html) return null;

      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : null;
    }

    /**
     * Shows a loading icon over a line item.
     * @param {string} line - Line item index.
     */
    enableLoading(line) {
      this.classList.add('pointer-events-none');

      const loader = this.querySelector(`#cart-item-${line} .cart-item__loader`);
      if (loader) loader.hidden = false;

      document.activeElement.blur();
      if (this.itemStatus) this.itemStatus.setAttribute('aria-hidden', 'false');
    }

    /**
     * Updates the cart recommendations.
     * @param {string} productId - The product id for which to find recommendations.
     */
    updateRecommendations(productId) {
      this.recommendations = this.recommendations || document.getElementById('cart-recommendations');
      if (!this.recommendations) return;

      if (productId) {
        this.recommendations.dataset.productId = productId;
        this.recommendations.init();
      } else {
        this.recommendations.innerHTML = '';
      }
    }

    /**
     * Updates the live regions.
     */
    updateLiveRegions() {
      if (this.itemStatus) this.itemStatus.setAttribute('aria-hidden', 'true');

      const cartStatus = this.querySelector('#cart-live-region-text');
      if (!cartStatus) return;
      cartStatus.setAttribute('aria-hidden', 'false');

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Traps focus in the relevant container or focuses the active element.
     * @param {number} line - Line item index.
     * @param {number} itemCount - Item count.
     * @param {string} name - Active element name.
     */
    setFocus(line, itemCount, name) {
      const lineItem = this.querySelector(`#cart-item-${line}`);
      let activeEl;

      if (lineItem) {
        activeEl = lineItem.querySelector(`[name="${name}"]`);
      }

      if (this.cartDrawer) {
        if (lineItem && activeEl) {
          trapFocus(this.cartDrawer, activeEl);
        } else if (itemCount === 0) {
          trapFocus(
            this.cartDrawer.querySelector('.js-cart-empty'),
            this.cartDrawer.querySelector('a')
          );
        } else if (this.cartDrawer.querySelector('.cart-item')) {
          trapFocus(this.cartDrawer, this.cartDrawer.querySelector('.js-item-name'));
        }
      } else if (lineItem && activeEl) {
        activeEl.focus();
      }
    }
  }

  customElements.define('cart-items', CartItems);
}
